import { Component, OnInit, DestroyRef } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Drawer } from 'primeng/drawer';
import { PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { Ripple } from 'primeng/ripple';
import { Tooltip } from 'primeng/tooltip';
import { CustomerService } from '../../../services/customer.service';
import { HelpBrowserComponent } from '../../help/help-browser/help-browser.component';

@Component({
  selector: 'app-manual-button',
  templateUrl: './manual-button.component.html',
  styleUrls: ['./manual-button.component.scss'],
  imports: [
    Drawer,
    HelpBrowserComponent,
    TranslatePipe,
    PrimeTemplate,
    ButtonDirective,
    Ripple,
    Tooltip
  ]
})
export class ManualButtonComponent implements OnInit {
  currentPageSlug = '';
  currentLang = '';
  drawerVisible = false;
  maximized = false;

  /** Chapter the drawer opens on: the current page's. */
  displayedSlug = '';

  private currentLaboratoryModule: string | undefined;
  private currentGender: string | undefined;

  /** route-to-slug map **/
  private urlMap: { [key: string]: string } = {
    /* home */
    '/home': 'home',

    /* user */
    '/profile': 'login',
    '/change/password': 'login',

    /* customers */
    '/customer/list': 'customer',
    '/customer/basic': 'customer-basic-info',
    '/customer/healthrecord': 'health-record',
    '/customer/examinations': 'examinations',
    '/customer/laboratorytests': 'laboratory-results',
    '/customer/prescriptions': 'prescriptions',
    '/customer/treatments': 'treatments',
    '/customer/pgt': 'pgt',
    '/customer/storage': 'storage',

    /* donors */
    '/donor/list': 'donors',
    '/donor/basic': 'donors',
    '/donor/external': 'donors',
    '/donor/healthrecord': 'health-record',
    '/donor/examinations': 'examinations',
    '/donor/laboratorytests': 'laboratory-results',
    '/donor/prescriptions': 'prescriptions',
    '/donor/treatments': 'treatments',
    '/donor/storage': 'storage',
    '/donor/donated-embryos': 'donated-embryos',

    /* other */
    '/schedule': 'appointments',
    '/appointment-period': 'appointment-period',
    '/schedule/resources': '6-resources',
    '/week-plan': '4-week-plan',
    '/invoices/list': 'billing',
    '/invoices/payroll': 'payroll',
    '/invoices/shopping-cart': 'open-invoice',
    '/donated-embryos': 'donated-embryos',
    '/clinic-management': 'clinic-management',
    '/clinic-management/treatment-cycles-overview': 'treatment-cycles-overview',
    '/clinic-management/lab-management': 'lab-management',
    '/clinic-management/lab-management/families': 'lab-action-families',

    /* mittaristo - one chapter per page, since the section header itself
       carries no route of its own */
    '/results/thl-data-transfer': 'thl-data-transfer',
    '/results/list4': 'annual-statistics',
    '/results/list5': 'treatment-variable-search',
    '/results/list7': 'treatment-results-summary',
    '/results/list8': 'lab-performance-metrics',
    '/results/list9': 'embryo-parameter-outcomes',

    /* settings */
    '/settings/basic': 'settings-general',
    '/settings/organizations': 'settings-organizations',
    '/settings/privileges': 'settings-privileges',
    '/settings/staff': 'settings-staff',
    '/settings/products': 'settings-products',
    '/settings/packages': 'settings-products',
    '/settings/drugs': 'settings-drugs',
    '/settings/laboratorytests': 'settings-laboratory-tests',
    '/settings/laboratory-test-packages': 'settings-laboratory-tests',
    '/settings/batches': 'settings-batch-numbers',
    '/settings/cryostorage': 'clinic-management',
    '/settings/hrtemplates': 'settings-record-templates',
    '/settings/medication-plan-templates': 'treatments',
    '/settings/feedback': 'settings-feedback-log',
    '/settings/log': 'settings-event-log',
    '/settings/contract-templates': 'settings-contract-templates'
  };

  /**
   * Basic-info panels with a chapter of their own. The open panel is the last
   * segment of /customer|donor/basic|external/:id/:panel.
   */
  private panelMap: { [panel: string]: string } = {
    'sec-shipments': 'sec-code'
  };

  constructor(
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    private customerService: CustomerService,
    private destroyRef: DestroyRef
  ) {
    this.currentLang =
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang() ||
      'fi';

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evt) => {
        this.currentLang = evt.lang;
      });

    /* reitin vaihto */
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => this.setHelpUrl(e.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.customerService.selectedCustomerObservable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cust) => {
        this.currentLaboratoryModule = cust?.laboratoryModule;
        this.currentGender = cust?.gender;
      });

    this.setHelpUrl(this.router.url);
  }

  private setHelpUrl(currentUrl: string): void {
    this.resolvePageSlug(currentUrl);
    this.displayedSlug = this.currentPageSlug;
  }

  private resolvePageSlug(currentUrl: string): void {
    if (
      currentUrl.startsWith('/customer/laboratorytests') ||
      currentUrl.startsWith('/donor/laboratorytests')
    ) {
      this.currentPageSlug = 'laboratory-results';
      return;
    }

    if (
      currentUrl.startsWith('/customer/laboratory/') ||
      currentUrl === '/customer/laboratory' ||
      currentUrl.startsWith('/donor/laboratory/') ||
      currentUrl === '/donor/laboratory'
    ) {
      // Käytä laboratoryModule tai fallback genderiin vanhoille asiakkaille
      const isIvf =
        this.currentLaboratoryModule === 'ivf-laboratory' ||
        (!this.currentLaboratoryModule && this.currentGender === 'female');
      const isSemen =
        this.currentLaboratoryModule === 'semen-laboratory' ||
        (!this.currentLaboratoryModule && this.currentGender === 'male');
      this.currentPageSlug = isIvf
        ? 'ivf-laboratory'
        : isSemen
          ? 'semen-laboratory'
          : '';
      return;
    }

    const panelMatch = currentUrl.match(
      /^\/(?:customer|donor)\/(?:basic|external)\/[^/]+\/([^/?#]+)/
    );
    if (panelMatch && this.panelMap[panelMatch[1]]) {
      this.currentPageSlug = this.panelMap[panelMatch[1]];
      return;
    }

    /* normaali mapping */
    // Pisin osuva etuliite voittaa, ei kartan järjestys: lapsireitti
    // (/schedule/resources) saa oman lukunsa eikä vanhempansa (/schedule).
    let bestPath = '';
    for (const path in this.urlMap) {
      if (currentUrl.startsWith(path) && path.length > bestPath.length) {
        bestPath = path;
      }
    }
    this.currentPageSlug = bestPath ? this.urlMap[bestPath] : '';
  }

  openHelp(): void {
    // Tabs and accordions change the URL with Location.go(), which fires no
    // router event, so resolve from the address the user sees right now.
    this.resolvePageSlug(this.location.path());
    if (!this.currentPageSlug) {
      return;
    }
    this.displayedSlug = this.currentPageSlug;
    this.drawerVisible = true;
  }

  toggleMaximize(): void {
    this.maximized = !this.maximized;
  }

  get drawerStyle(): { [klass: string]: string } {
    return { width: this.maximized ? '100vw' : '70vw' };
  }
}
